import React, { useState, useEffect, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nContext } from './I18nContext';
import { I18nContextType, LanguageCode } from '../types/i18n.types';
import { SUPPORTED_LANGUAGES, LANGUAGE_STORAGE_KEY, isValidLanguageCode } from '../config/languages';

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  // Initialize language on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage && isValidLanguageCode(savedLanguage) && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Handle language change with error handling and loading state
  const changeLanguage = useCallback(async (language: LanguageCode): Promise<void> => {
    if (language === i18n.language) return;

    setIsChangingLanguage(true);

    try {
      await i18n.changeLanguage(language);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);

      // Update document language attribute for screen readers and SEO
      document.documentElement.lang = language;

      // Update document direction for RTL languages (future-proofing)
      const languageConfig = SUPPORTED_LANGUAGES.find(lang => lang.code === language);
      if (languageConfig) {
        document.documentElement.dir = languageConfig.dir;
      }

      // Dispatch custom event for other components to listen to
      window.dispatchEvent(new CustomEvent('languageChanged', {
        detail: { language, previousLanguage: i18n.language }
      }));

    } catch (error) {
      console.error('Failed to change language:', error);

      // Show user-friendly error message
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(`Failed to change language to ${language}. Please try again.`);
      }

      throw error; // Re-throw for component handling
    } finally {
      setIsChangingLanguage(false);
    }
  }, [i18n]);

  // Handle system language changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LANGUAGE_STORAGE_KEY && e.newValue && isValidLanguageCode(e.newValue)) {
        if (e.newValue !== i18n.language) {
          i18n.changeLanguage(e.newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [i18n]);

  // Context value with memoization to prevent unnecessary re-renders
  const contextValue: I18nContextType = React.useMemo(() => ({
    currentLanguage: (i18n.language || 'vi') as LanguageCode,
    changeLanguage,
    isChangingLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    isRTL: i18n.dir() === 'rtl'
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n object is stable; i18n.language and i18n.dir are the actual changing values
  }), [i18n.language, changeLanguage, isChangingLanguage, i18n.dir]);

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
};